/**
 * Shared cell renderers for Skimmer sync error exception configs.
 *
 * Both the invoice (sale domain) and customer (client domain) configs
 * surface days-open and the last-reported-at + linked CSV in the same
 * way. Keep the renderers in one place so they stay aligned across
 * inspector and platform-report variants.
 */
import React from 'react'
import { Link, Tooltip, Typography } from '@mui/material'
import { withTooltip } from './muiElements'
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'
import {
  formatBusinessDate,
  formatDaysOpen,
  daysOpenColor,
} from '../../../utils/inspectorFormatters'

// Backend SK ingest backfills the last N days of CSVs (DEFAULT_BACKFILL_DAYS
// in shared/sk_sync_error_reports/ingest_service.py). Anything that has been
// open for the full window could actually be older, so cap the display at
// "N+ days" rather than implying an exact count.
const SK_BACKFILL_DAYS = 14

export const renderDaysOpen = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const days = typeof row.days_open === 'number' ? row.days_open : null
  const text = days !== null && days >= SK_BACKFILL_DAYS
    ? `${SK_BACKFILL_DAYS}+ days`
    : formatDaysOpen(row.first_reported_at)
  return React.createElement(
    Typography,
    {
      variant: 'body2',
      sx: { color: daysOpenColor(days) },
    },
    text,
  )
}

export const renderLastReportedLink = (
  value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const formatted = formatBusinessDate(value)
  if (!row.last_seen_csv_url) return formatted
  const filename = row.last_seen_csv
  return withTooltip(
    { title: filename || 'Open in Drive', arrow: true },
    React.createElement(
      Link,
      {
        href: row.last_seen_csv_url,
        target: '_blank',
        rel: 'noopener noreferrer',
        sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 },
      },
      formatted,
      React.createElement(OpenInNewIcon, { fontSize: 'inherit' }),
    ),
  )
}
